import { useCallback } from "react";
import { toast } from "sonner";
import { useApiClient } from "../contexts/ApiContext";
import { TaskId, UserId } from "../db/schema";

/**
 * Changes who owns a task.
 *
 * Goes through the API rather than a sync mutation. A sync push sends the whole
 * task row from this tab's cache, so a copy predating a server-side escalation
 * would hand the task back to the agent as a side effect — and being a task-row
 * write rather than an activity, it recorded nothing. The server ignores
 * ownership on sync updates now, making this the only path.
 *
 * Two consequences worth knowing at the call site: ownership in the UI refreshes
 * on the next sync pull rather than optimistically, and because there is no
 * local write, a failure would otherwise be indistinguishable from nothing
 * happening — hence the toast.
 */
export function useSetTaskAssignee(taskId: TaskId) {
	const apiClient = useApiClient();

	return useCallback(
		async (
			assignedTo: UserId | undefined,
			assigneeType: "user" | "agent" = "user",
		) => {
			try {
				await apiClient.admin.setTaskAssignee({
					taskId,
					assigneeType,
					assignedTo,
				});
			} catch (error) {
				console.error("Failed to change task assignee:", error);
				toast.error("Couldn't change who this task is assigned to");
			}
		},
		[apiClient, taskId],
	);
}
